import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Save, Plus, Trash2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const FooterManager = () => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    about_text: '',
    contact_email: '',
    contact_phone: '',
    social_links: {},
    footer_links: [],
    footer_links_title: 'Quick Links',
    link_style: {
      text_color: 'text-gray-900',
      text_color_dark: 'dark:text-gray-200',
      font_weight: 'font-medium',
      heading_size: 'text-lg',
      link_size: 'text-sm'
    }
  });
  const [newLink, setNewLink] = useState({ label: '', url: '' });

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/footer`);
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching footer settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/footer`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Footer settings updated successfully' });
        fetchSettings();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update footer settings', variant: 'destructive' });
    }
  };

  const handleAddLink = () => {
    if (newLink.label && newLink.url) {
      setSettings({
        ...settings,
        footer_links: [...(settings.footer_links || []), newLink]
      });
      setNewLink({ label: '', url: '' });
    }
  };

  const handleRemoveLink = (index) => {
    const newLinks = [...settings.footer_links];
    newLinks.splice(index, 1);
    setSettings({ ...settings, footer_links: newLinks });
  };

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-3 sm:p-4 lg:p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Footer Management</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm sm:text-base">Manage footer content and links</p>
        </div>
        <Button onClick={handleSave} className="bg-gradient-to-r from-red-600 to-black">
          <Save className="w-4 h-4 mr-2" />
          Save Changes
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* About Section */}
        <Card>
          <CardHeader>
            <CardTitle>About Section</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>About Text</Label>
              <Textarea
                value={settings.about_text}
                onChange={(e) => setSettings({ ...settings, about_text: e.target.value })}
                placeholder="Short description about your platform"
                rows={4}
                className="mt-2"
              />
            </div>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card>
          <CardHeader>
            <CardTitle>Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Contact Email</Label>
              <Input
                type="email"
                value={settings.contact_email}
                onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                placeholder="welcome@trainerlist.com"
                className="mt-2"
              />
            </div>
            <div>
              <Label>Contact Phone</Label>
              <Input
                value={settings.contact_phone}
                onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })}
                placeholder="+1 (555) 123-4567"
                className="mt-2"
              />
            </div>
          </CardContent>
        </Card>

        {/* Footer Links */}
        <Card>
          <CardHeader>
            <CardTitle>Footer Links</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Section Title Input */}
            <div>
              <Label>Section Title</Label>
              <Input
                placeholder="e.g., Quick Links, Useful Links, Resources"
                value={settings.footer_links_title || 'Quick Links'}
                onChange={(e) => setSettings({ ...settings, footer_links_title: e.target.value })}
                className="mt-2"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">This title will appear above the footer links</p>
            </div>

            <div className="border-t pt-4"></div>

            {(settings.footer_links || []).map((link, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="flex-1">
                  <p className="font-medium">{link.label}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{link.url}</p>
                </div>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleRemoveLink(index)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}

            <div className="border-t pt-4 mt-4">
              <Label>Add New Link</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Input
                  placeholder="Label"
                  value={newLink.label}
                  onChange={(e) => setNewLink({ ...newLink, label: e.target.value })}
                />
                <Input
                  placeholder="URL"
                  value={newLink.url}
                  onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                />
              </div>
              <Button onClick={handleAddLink} size="sm" className="mt-2">
                <Plus className="w-4 h-4 mr-1" />
                Add Link
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer Link Styling */}
        <Card>
          <CardHeader>
            <CardTitle>Footer Link Styling</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Text Color (Light Mode)</Label>
              <select
                value={settings.link_style?.text_color || 'text-gray-900 dark:text-white'}
                onChange={(e) => setSettings({ 
                  ...settings, 
                  link_style: { ...settings.link_style, text_color: e.target.value } 
                })}
                className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="text-gray-600 dark:text-gray-400">Light Gray (Transparent)</option>
                <option value="text-gray-900 dark:text-white">Dark Gray (Clear)</option>
                <option value="text-black">Black (Very Dark)</option>
                <option value="text-red-700">Red</option>
              </select>
            </div>
            
            <div>
              <Label>Text Color (Dark Mode)</Label>
              <select
                value={settings.link_style?.text_color_dark || 'dark:text-gray-200'}
                onChange={(e) => setSettings({ 
                  ...settings, 
                  link_style: { ...settings.link_style, text_color_dark: e.target.value } 
                })}
                className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="dark:text-gray-400">Gray 400 (Transparent)</option>
                <option value="dark:text-gray-200">Gray 200 (Clear)</option>
                <option value="dark:text-white">White (Very Clear)</option>
              </select>
            </div>

            <div>
              <Label>Font Weight</Label>
              <select
                value={settings.link_style?.font_weight || 'font-medium'}
                onChange={(e) => setSettings({ 
                  ...settings, 
                  link_style: { ...settings.link_style, font_weight: e.target.value } 
                })}
                className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="font-normal">Normal</option>
                <option value="font-medium">Medium (Recommended)</option>
                <option value="font-semibold dark:text-white">Semi Bold</option>
                <option value="font-bold dark:text-white">Bold</option>
              </select>
            </div>

            <div>
              <Label>Heading Font Size (About Us, Follow Us, etc.)</Label>
              <select
                value={settings.link_style?.heading_size || 'text-lg'}
                onChange={(e) => setSettings({ 
                  ...settings, 
                  link_style: { ...settings.link_style, heading_size: e.target.value } 
                })}
                className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="text-xs">Extra Small (12px)</option>
                <option value="text-sm">Small (14px)</option>
                <option value="text-base">Base (16px)</option>
                <option value="text-lg">Large (18px) - Recommended</option>
                <option value="text-xl">Extra Large (20px)</option>
                <option value="text-2xl">2XL (24px)</option>
              </select>
            </div>

            <div>
              <Label>Link Font Size (Privacy Policy, etc.)</Label>
              <select
                value={settings.link_style?.link_size || 'text-sm'}
                onChange={(e) => setSettings({ 
                  ...settings, 
                  link_style: { ...settings.link_style, link_size: e.target.value } 
                })}
                className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="text-xs">Extra Small (12px)</option>
                <option value="text-sm">Small (14px) - Recommended</option>
                <option value="text-base">Base (16px)</option>
                <option value="text-lg">Large (18px)</option>
                <option value="text-xl">Extra Large (20px)</option>
              </select>
            </div>

            <div className="bg-blue-50 dark:bg-gray-700 p-4 rounded-lg mt-4 border border-blue-200 dark:border-gray-600">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                <strong>Preview:</strong> When you save changes, the footer links color, weight, and size will be updated.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FooterManager;